import React, { FunctionComponent, JSX, useEffect, useRef, useState } from "react";

import { FIELD_TYPES, FormFieldDefinition, fieldTypeLabel, isSectionField, TITLE_SIZES } from "./fieldTypes";
import { CopyIcon, DragIcon, TrashIcon } from "../common/sidebar/icons";
import { WysiwygFormWidget } from "../settings/widget/WysiwygFormWidget";
import { SubscriptionType } from "../../models/subscription/SubscriptionType";
import { MOBILE_QUERY, useMediaQuery } from "../../hooks/useMediaQuery";

interface Props {
  fields: FormFieldDefinition[];
  onChange: (fields: FormFieldDefinition[]) => void;
  subscriptionType?: SubscriptionType;
}

interface FieldGroup {
  /** Index des Abschnittsfeldes in `fields`, oder -1 für Felder vor dem ersten Abschnitt. */
  headerIndex: number;
  name: string;
  indices: number[];
}

const groupFields = (fields: FormFieldDefinition[]): FieldGroup[] => {
  const groups: FieldGroup[] = [{ headerIndex: -1, name: "Formularfelder", indices: [] }];

  fields.forEach((field, index) => {
    if (isSectionField(field)) {
      groups.push({ headerIndex: index, name: field.description || field.title, indices: [] });
    } else {
      groups[groups.length - 1].indices.push(index);
    }
  });

  return groups.filter((group) => group.headerIndex >= 0 || group.indices.length > 0);
};

const uniqueTitle = (fields: FormFieldDefinition[], base: string): string => {
  if (!fields.some((field) => field.title === base)) return base;
  let counter = 2;
  while (fields.some((field) => field.title === `${base} ${counter}`)) counter++;
  return `${base} ${counter}`;
};

export const FormFieldsEditor: FunctionComponent<Props> = ({ fields, onChange, subscriptionType }) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const inspectorRef = useRef<HTMLElement>(null);
  const isMobile = useMediaQuery(MOBILE_QUERY);

  // Auf Mobile steht der Inspektor unter der Liste — nach der Auswahl dorthin scrollen.
  const selectField = (index: number): void => {
    setSelectedIndex(index);
    if (isMobile) {
      requestAnimationFrame(() => inspectorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  };

  // Felder werden asynchron geladen — dann das erste Feld vorauswählen.
  useEffect(() => {
    setSelectedIndex((current) => (current < 0 && fields.length > 0 ? 0 : current));
  }, [fields.length]);

  const selected = selectedIndex >= 0 ? fields[selectedIndex] : undefined;

  const updateSelected = (patch: Partial<FormFieldDefinition>): void => {
    onChange(fields.map((field, index) => (index === selectedIndex ? { ...field, ...patch } : field)));
  };

  const addField = (): void => {
    const field: FormFieldDefinition = {
      title: uniqueTitle(fields, "Neues Feld"),
      fieldType: "string",
      fieldRequired: false
    };
    onChange([...fields, field]);
    setSelectedIndex(fields.length);
  };

  const duplicateField = (index: number): void => {
    const copy = { ...fields[index], title: uniqueTitle(fields, `${fields[index].title} Kopie`) };
    const next = [...fields];
    next.splice(index + 1, 0, copy);
    onChange(next);
    setSelectedIndex(index + 1);
  };

  const removeField = (index: number): void => {
    if (!window.confirm(`Feld "${fields[index].title}" wirklich entfernen?`)) return;
    onChange(fields.filter((_, i) => i !== index));
    setSelectedIndex((current) => (current >= index ? Math.max(0, current - 1) : current));
  };

  const moveField = (from: number, to: number): void => {
    if (from === to) return;
    const next = [...fields];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
    setSelectedIndex(to);
  };

  const renderActions = (index: number): JSX.Element => (
    <div className="data-row__actions">
      <button
        type="button"
        title="Duplizieren"
        className="btn btn-icon"
        onClick={(event) => {
          event.stopPropagation();
          duplicateField(index);
        }}>
        <CopyIcon size={13} />
      </button>
      <button
        type="button"
        title="Entfernen"
        className="btn btn-icon btn-icon-danger"
        onClick={(event) => {
          event.stopPropagation();
          removeField(index);
        }}>
        <TrashIcon size={13} />
      </button>
    </div>
  );

  const dragProps = (
    index: number
  ): Pick<React.HTMLAttributes<HTMLElement>, "onDragOver" | "onDrop" | "onDragEnd"> & { draggable: true } => ({
    draggable: true,
    onDragOver: (event) => event.preventDefault(),
    onDrop: (event) => {
      event.preventDefault();
      if (draggedIndex !== null) moveField(draggedIndex, index);
      setDraggedIndex(null);
    },
    onDragEnd: () => setDraggedIndex(null)
  });

  const renderFieldRow = (index: number): JSX.Element => {
    const field = fields[index];
    return (
      <div
        key={`${field.title}-${index}`}
        className={`field-row${index === selectedIndex ? " is-selected" : ""}`}
        onClick={() => selectField(index)}
        onDragStart={() => setDraggedIndex(index)}
        {...dragProps(index)}>
        <DragIcon size={12} />
        <span className="field-row__label">{field.title}</span>
        <span className="field-row__type">{fieldTypeLabel(field.fieldType)}</span>
        <span className="field-row__required">{field.fieldRequired ? "Pflicht" : ""}</span>
        {renderActions(index)}
      </div>
    );
  };

  const groups = groupFields(fields);

  return (
    <div className="field-editor">
      <section className="panel">
        <div className="panel__head panel__head--tinted">
          <span className="panel__meta-strong">
            {fields.length} Felder · {groups.length} Abschnitte
          </span>
          <span className="panel__meta">Zum Bearbeiten Feld anklicken</span>
        </div>

        {groups.map((group) => (
          <div key={group.headerIndex}>
            {group.headerIndex >= 0 && (
              <div
                className={`field-group${group.headerIndex === selectedIndex ? " is-selected" : ""}`}
                onClick={() => selectField(group.headerIndex)}
                onDragStart={() => setDraggedIndex(group.headerIndex)}
                {...dragProps(group.headerIndex)}>
                <span className="field-group__name">{group.name}</span>
                <span className="field-group__count">{group.indices.length} Felder</span>
                {renderActions(group.headerIndex)}
              </div>
            )}
            {group.indices.map((index) => renderFieldRow(index))}
          </div>
        ))}

        <div className="panel__bar">
          <button type="button" className="btn btn-secondary btn-sm" onClick={addField}>
            + Feld hinzufügen
          </button>
        </div>
      </section>

      <aside className="panel field-inspector" ref={inspectorRef}>
        <div className="panel__head panel__head--tinted">
          <h2>Feld bearbeiten</h2>
          <span className="panel__meta">{selected ? fieldTypeLabel(selected.fieldType) : ""}</span>
        </div>

        {!selected && <div className="panel__body text-muted">Wählen Sie links ein Feld aus.</div>}

        {selected && (
          <div className="panel__body field-inspector__body">
            <label className="field-inspector__label">
              <span>Bezeichnung</span>
              <input
                className="form-control"
                value={selected.title}
                onChange={(event) => updateSelected({ title: event.target.value })}
              />
              <small className="form-text">
                Der Name ist zugleich der Datenschlüssel — bei Feldern mit bestehenden Anmeldungen nicht ändern.
              </small>
            </label>

            {selected.fieldType !== "markdown" && (
              <label className="field-inspector__label">
                <span>{selected.fieldType === "title" ? "Abschnittstitel" : "Hilfetext"}</span>
                <input
                  className="form-control"
                  placeholder="optional"
                  value={selected.description || ""}
                  onChange={(event) => updateSelected({ description: event.target.value })}
                />
              </label>
            )}

            <div className="field-inspector__grid">
              <label className="field-inspector__label">
                <span>Typ</span>
                <select
                  className="form-select"
                  value={selected.fieldType}
                  onChange={(event) => updateSelected({ fieldType: event.target.value })}>
                  {FIELD_TYPES.map((type) => (
                    <option key={type.value} value={type.value} disabled={type.plusOnly && !subscriptionType}>
                      {type.label}
                      {type.plusOnly && !subscriptionType ? " (Plus)" : ""}
                    </option>
                  ))}
                </select>
              </label>

              {selected.fieldType === "title" && (
                <label className="field-inspector__label">
                  <span>Grösse</span>
                  <select
                    className="form-select"
                    value={selected.size || "h2"}
                    onChange={(event) => updateSelected({ size: event.target.value })}>
                    {TITLE_SIZES.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            {selected.fieldType === "select" && (
              <label className="field-inspector__label">
                <span>Auswahlmöglichkeiten (eine pro Zeile)</span>
                <textarea
                  className="form-control"
                  rows={4}
                  value={(selected.values || []).join("\n")}
                  onChange={(event) =>
                    updateSelected({ values: event.target.value.split("\n").filter((value) => value.trim() !== "") })
                  }
                />
              </label>
            )}

            {selected.fieldType === "date" && (
              <div className="field-inspector__grid">
                <label className="field-inspector__label">
                  <span>Frühestes Datum</span>
                  <input
                    type="date"
                    className="form-control"
                    value={selected.minDate || ""}
                    onChange={(event) => updateSelected({ minDate: event.target.value })}
                  />
                </label>
                <label className="field-inspector__label">
                  <span>Spätestes Datum</span>
                  <input
                    type="date"
                    className="form-control"
                    value={selected.maxDate || ""}
                    onChange={(event) => updateSelected({ maxDate: event.target.value })}
                  />
                </label>
              </div>
            )}

            {selected.fieldType === "zipCode" && (
              <label className="field-inspector__label">
                <span>Erlaubte Postleitzahlen</span>
                <input
                  className="form-control"
                  placeholder="5200, 5210, 5116"
                  value={selected.zipCodes || ""}
                  onChange={(event) => updateSelected({ zipCodes: event.target.value })}
                />
              </label>
            )}

            {selected.fieldType === "zipCode" && (
              <label className="field-inspector__label">
                <span>Meldung bei nicht erlaubter PLZ</span>
                <input
                  className="form-control"
                  placeholder="Eine Anmeldung von Ihrem Wohnort ist nicht möglich."
                  value={selected.zipCodeErrorMessage || ""}
                  onChange={(event) => updateSelected({ zipCodeErrorMessage: event.target.value })}
                />
                <small className="form-text">
                  Leer lassen für die Standardmeldung. Ist die Warteliste aktiv, erscheint stattdessen die
                  Wartelisten-Meldung aus den Formular-Einstellungen.
                </small>
              </label>
            )}

            {selected.fieldType === "markdown" && (
              <div className="field-inspector__label">
                <span>Formatierter Text</span>
                <WysiwygFormWidget
                  id={`markdown-${selectedIndex}`}
                  label={"Formatierter Text"}
                  value={selected.markdownDescription}
                  onChange={(value) => updateSelected({ markdownDescription: value })}
                />
              </div>
            )}

            {selected.fieldType !== "title" && selected.fieldType !== "markdown" && (
              <div className="field-inspector__toggle-row">
                <span>Pflichtfeld</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!selected.fieldRequired}
                  aria-label="Pflichtfeld"
                  className={`toggle${selected.fieldRequired ? " is-on" : ""}`}
                  onClick={() => updateSelected({ fieldRequired: !selected.fieldRequired })}>
                  <span className="toggle__knob" />
                </button>
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
};
